import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MethodBadge from './MethodBadge';
import { PortalToolOut as PortalTool } from '../types/apiRequests';
import { useTranslation } from '../i18n/useTranslation';

function ToolRow({ tool }: { tool: PortalTool }) {
  const t = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasParams = tool.parameters && tool.parameters.length > 0;

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: hasParams ? 'pointer' : 'default',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
        }}
        onClick={() => hasParams && setExpanded(!expanded)}
      >
        <TableCell sx={{ width: 65, py: 1.5 }}>
          <MethodBadge method={tool.http_method} />
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, fontSize: '0.8rem', color: 'text.primary' }}>
            {tool.path}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.83rem' }}>
            {tool.description}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 36, py: 1.5 }}>
          {hasParams && (
            <IconButton size="small" sx={{ color: 'text.disabled' }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {hasParams && (
        <TableRow>
          <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
            <Collapse in={expanded}>
              <Box sx={{ py: 1.5, pl: 2, pb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('toolsTable.col.name')}</TableCell>
                      <TableCell>{t('toolsTable.col.type')}</TableCell>
                      <TableCell>{t('toolsTable.col.required')}</TableCell>
                      <TableCell>{t('toolsTable.col.description')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(tool.parameters ?? []).map((p, i) => (
                      <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem' }}>{p.name}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{p.type || 'string'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{p.required ? t('common.yes') : t('common.no')}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{p.description || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

interface Props {
  tools: PortalTool[];
  toolsByTag: Record<string, PortalTool[]>;
}

export default function ToolsTable({ tools, toolsByTag }: Props) {
  const t = useTranslation();
  return (
    <Card sx={{ overflow: 'hidden' }}>
      {tools.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.disabled' }}>{t('toolsTable.empty')}</Typography>
        </Box>
      ) : (
        Object.entries(toolsByTag).map(([tag, tagTools]) => (
          <Box key={tag}>
            <Box sx={{ px: 3, py: 1.25, bgcolor: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {tag}
              </Typography>
            </Box>
            <Table size="small">
              <TableBody>
                {tagTools.map((tool) => (
                  <ToolRow key={tool.id} tool={tool} />
                ))}
              </TableBody>
            </Table>
          </Box>
        ))
      )}
    </Card>
  );
}
